import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Select, Switch, message } from 'antd';
import Breadcrumbs from '../../components/Breadcrumbs';
import { StakeholderIcon, SaveIcon } from '../../assets/icons';
import { useGetUsersQuery, userName } from '../../features/users/usersApi';
import {
  useGetTeamQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  teamName,
  teamDesc,
  teamUserIds,
  type Team,
} from '../../features/teams/teamsApi';

const INPUT =
  'w-full rounded-lg border border-[#c3c6d7] bg-white px-4 py-3 text-base text-ink placeholder:text-[#6b7280] transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15';
const ERR = 'text-xs font-medium text-[#ba1a1a]';

interface TeamFormValues {
  name: string;
  users: number[];
  description: string;
  active: boolean;
}

const validationSchema = Yup.object({
  name: Yup.string().trim().required('Team name is required'),
  users: Yup.array().min(1, 'Select at least one user'),
});

function FieldLabel({ children, required }: { children: ReactNode; required?: boolean }) {
  return (
    <span className="text-sm font-semibold text-ink-900">
      {children}
      {required && <span className="text-[#ba1a1a]"> *</span>}
    </span>
  );
}

export default function CreateTeamPage() {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = Boolean(editId);

  // Users dropdown — server-side search, full list (no pagination limit).
  const [userSearchInput, setUserSearchInput] = useState('');
  const [userSearch, setUserSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setUserSearch(userSearchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [userSearchInput]);
  const { data: usersData } = useGetUsersQuery({
    isActive: true,
    all: true,
    ...(userSearch ? { search: userSearch } : {}),
  });
  // Accumulate id → name so selected users keep their labels when a search narrows the list.
  const [nameMap, setNameMap] = useState<Record<number, string>>({});
  useEffect(() => {
    if (!usersData?.items) return;
    setNameMap((prev) => {
      const next = { ...prev };
      usersData.items.forEach((u) => {
        next[u.id] = userName(u);
      });
      return next;
    });
  }, [usersData]);

  const { data: editTeam } = useGetTeamQuery(editId!, { skip: !isEdit });
  const [createTeam, { isLoading: isCreating }] = useCreateTeamMutation();
  const [updateTeam, { isLoading: isUpdating }] = useUpdateTeamMutation();
  const saving = isCreating || isUpdating;

  const formik = useFormik<TeamFormValues>({
    initialValues: { name: '', users: [], description: '', active: true },
    validationSchema,
    onSubmit: async (values) => {
      const payload = {
        name: values.name.trim(),
        description: values.description.trim(),
        users: values.users,
        status: values.active ? 'Active' : 'Inactive',
      };
      try {
        if (isEdit && editId) {
          await updateTeam({ id: editId, patch: payload }).unwrap();
        } else {
          await createTeam(payload).unwrap();
        }
        navigate('/admin/teams');
      } catch {
        message.error(`Could not ${isEdit ? 'update' : 'create'} the team. Please try again.`);
      }
    },
  });

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, setFieldTouched } =
    formik;
  const errorOf = (name: keyof TeamFormValues) =>
    touched[name] && errors[name] ? (errors[name] as string) : undefined;

  // Options = the fetched users, plus any selected user the current search dropped.
  const userOptions = useMemo(() => {
    const fetched = (usersData?.items ?? []).map((u) => ({ value: u.id, label: userName(u) }));
    const ids = new Set(fetched.map((o) => o.value));
    const extra = values.users
      .filter((id) => !ids.has(id))
      .map((id) => ({ value: id, label: nameMap[id] || `User ${id}` }));
    return [...fetched, ...extra];
  }, [usersData, values.users, nameMap]);

  // Edit mode: prefill from the loaded team.
  useEffect(() => {
    if (!editTeam) return;
    const t = editTeam as Team;
    setFieldValue('name', teamName(t) === 'Untitled team' ? '' : teamName(t));
    setFieldValue('description', teamDesc(t));
    setFieldValue('users', teamUserIds(t));
    setFieldValue('active', (t.status || 'Active').toLowerCase() !== 'inactive');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTeam]);

  return (
    <form onSubmit={handleSubmit} noValidate className="flex w-full flex-col gap-6">
      {/* Heading */}
      <div className="flex flex-col gap-1">
        <Breadcrumbs
          items={[
            { label: 'Users', to: '/admin/users' },
            { label: 'Teams', to: '/admin/teams' },
            { label: isEdit ? 'Edit Team' : 'Create Team' },
          ]}
        />
        <h1 className="text-3xl font-bold tracking-[-0.64px] text-ink-900">
          {isEdit ? 'Edit Team' : 'Create Team'}
        </h1>
        <p className="text-base text-ink-secondary">
          Group users into a team to coordinate work across the CHUNAAV platform.
        </p>
      </div>

      {/* Form card */}
      <div className="overflow-hidden rounded-xl border border-line/30 bg-white shadow-card">
        {/* Card header */}
        <div className="flex items-center justify-between border-b border-line/30 bg-brand-shade px-6 py-5">
          <div className="flex items-center gap-3">
            <span className="flex size-10 items-center justify-center rounded-lg bg-brand/10 text-brand">
              <StakeholderIcon className="size-5" />
            </span>
            <h2 className="text-xl font-semibold tracking-[-0.2px] text-ink-900">Team Details</h2>
          </div>
        </div>

        <div className="flex flex-col gap-8 p-6">
          {/* Team Name | Select Users */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <label className="flex flex-col gap-2">
              <FieldLabel required>Team Name</FieldLabel>
              <input
                name="name"
                placeholder="e.g. Media Team"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                className={[INPUT, errorOf('name') ? 'border-red-400' : ''].join(' ')}
              />
              {errorOf('name') && <span className={ERR}>{errorOf('name')}</span>}
            </label>

            <div className="flex flex-col gap-2">
              <FieldLabel required>Select Users</FieldLabel>
              <Select
                mode="multiple"
                className="w-full"
                placeholder="Search and add users"
                showSearch
                filterOption={false}
                onSearch={setUserSearchInput}
                status={errorOf('users') ? 'error' : undefined}
                value={values.users}
                onChange={(v: number[]) => setFieldValue('users', v)}
                onBlur={() => setFieldTouched('users', true)}
                options={userOptions}
              />
              {errorOf('users') && <span className={ERR}>{errorOf('users')}</span>}
            </div>
          </div>

          {/* Description */}
          <label className="flex flex-col gap-2">
            <FieldLabel>Description</FieldLabel>
            <textarea
              name="description"
              rows={4}
              placeholder="Describe the purpose of this team..."
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              className="w-full resize-none rounded-lg border border-[#c2c6d6] bg-brand-shade px-4 py-3 text-sm text-ink placeholder:text-[#9ca3af] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
            />
          </label>

          {/* Status toggle */}
          <div className="flex items-center justify-between rounded-xl border border-line/20 bg-brand-shade p-4">
            <div className="flex items-center gap-4">
              <span className="size-3 shrink-0 rounded-full bg-brand" />
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-ink-900">Status</span>
                <span className="text-sm text-ink-secondary">
                  Team will be{' '}
                  <span className="font-medium text-brand">{values.active ? 'Active' : 'Inactive'}</span>
                </span>
              </div>
            </div>
            <Switch checked={values.active} onChange={(c) => setFieldValue('active', c)} />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-4 pt-2">
            <button
              type="button"
              onClick={() => navigate('/admin/teams')}
              className="rounded-lg border border-[#c3c6d7] px-8 py-3 text-sm font-semibold text-ink-secondary transition-colors hover:bg-brand-shade"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 rounded-lg bg-brand px-8 py-3 text-sm font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? 'Saving…' : isEdit ? 'Update Team' : 'Save Team'}
              <SaveIcon className="size-[15px]" />
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}
